import { useEffect, useRef, type ReactNode } from 'react'

import { Button } from '@/ui/Button'
import { cn } from '@/ui/lib/utils'

interface DialogProps {
  /** Accessible name for the dialog (`aria-labelledby` target). */
  titleId: string
  title: ReactNode
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Extra classes for the dialog box (e.g. a wider, fixed-height box). */
  className?: string
  /** Replaces the default `max-w-lg` width class (spec 036: the 440px indicator modal). */
  widthClassName?: string
  /** Replaces the default `bg-ink/70` backdrop class. */
  backdropClassName?: string
  /** When true a press-and-release on the backdrop itself closes the dialog. Default false. */
  closeOnBackdrop?: boolean
}

/**
 * A shared, manually-built modal dialog (spec 003 §5) — `role="dialog"`,
 * `aria-modal="true"`, a focus trap on open, `Escape`-to-close, and a
 * labelled close button. No new dependency: React 19 + native DOM APIs only,
 * per `.claude/rules/react.md`'s "no new runtime dependency without asking."
 *
 * Both the conversation-level inspect popup and the per-message detail
 * popup (Phase 5.2/5.3) render one instance of this component each — it owns
 * no data of its own, only the dialog chrome and its accessibility behaviour.
 */
export function Dialog({
  titleId,
  title,
  open,
  onClose,
  children,
  className,
  widthClassName = 'max-w-lg',
  backdropClassName = 'bg-ink/70',
  closeOnBackdrop = false,
}: DialogProps) {
  const backdropPressRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  // Focus management: move focus into the dialog on open, and restore it to
  // whatever was focused beforehand on close — a keyboard user is never left
  // stranded outside the dialog, nor stranded on a since-removed element.
  useEffect(() => {
    if (!open) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    closeButtonRef.current?.focus()

    return () => {
      previouslyFocused?.focus()
    }
  }, [open])

  // Escape-to-close and a same-dialog focus trap, scoped to this dialog's own
  // subtree so a Tab/Shift+Tab press can never escape into the page behind it.
  useEffect(() => {
    if (!open) return

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab') return

      const container = containerRef.current
      if (!container) return

      const focusable = container.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', backdropClassName)}
      onMouseDown={(event) => {
        backdropPressRef.current = event.target === event.currentTarget
      }}
      onClick={(event) => {
        if (closeOnBackdrop && backdropPressRef.current && event.target === event.currentTarget) onClose()
        backdropPressRef.current = false
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'flex max-h-[80vh] w-full flex-col gap-4 overflow-y-auto border border-ink-14 bg-paper p-4 text-ink',
          widthClassName,
          className,
        )}
      >
        <div className="flex items-center justify-between gap-4 border-b border-ink-14 pb-3">
          <h2
            id={titleId}
            className="font-sans text-lg font-semibold tracking-wide text-ink uppercase"
          >
            {title}
          </h2>
          <Button
            ref={closeButtonRef}
            type="button"
            variant="secondary"
            aria-label="Close"
            onClick={onClose}
            className="shrink-0 border-ink-25 px-2 py-1 font-mono text-xs font-normal tracking-normal text-ink normal-case hover:bg-accent-wash"
          >
            ×
          </Button>
        </div>

        {children}
      </div>
    </div>
  )
}
